import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../api/client';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Progress } from '../components/ui/progress';
import { ArrowLeft, ArrowRight, AlertTriangle } from 'lucide-react';

export default function RunQuestionPage() {
  const { runId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [run, setRun] = useState(null);
  const [template, setTemplate] = useState(null);
  const [question, setQuestion] = useState(null);
  const [selected, setSelected] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [versionWarning, setVersionWarning] = useState(false);
  const [needsProjectFields, setNeedsProjectFields] = useState(false);
  const [projectFields, setProjectFields] = useState({});

  const loadRun = useCallback(async () => {
    try {
      const res = await api.get(`/runs/${runId}`);
      const runData = res.data;
      setRun(runData);

      if (runData.status === 'complete') { navigate(`/checklists/${runId}/complete`); return; }

      const tplRes = await api.get(`/templates/${runData.template_id}`);
      const tpl = tplRes.data;
      setTemplate(tpl);

      if (runData.duplicated_from_run_id && (!runData.project_fields || Object.keys(runData.project_fields).length === 0)) {
        setNeedsProjectFields(true);
        if (runData.version_mismatch) setVersionWarning(true);
        const defaults = {};
        (tpl.project_fields || []).forEach(f => {
          if (f.auto_fill === 'user.name' && user?.name) defaults[f.key] = user.name;
          else defaults[f.key] = '';
        });
        setProjectFields(defaults);
        setLoading(false);
        return;
      }

      if (runData.duplicated_from_run_id && runData.version_mismatch) setVersionWarning(true);

      const questions = (tpl.questions || []).sort((a, b) => a.order - b.order);
      const answeredIds = (runData.answers || []).map(a => a.question_id);

      let nextQ = null;
      if (answeredIds.length === 0) {
        nextQ = questions[0];
      } else {
        const lastAnswer = runData.answers[runData.answers.length - 1];
        const lastQ = questions.find(q => q.id === lastAnswer.question_id);
        if (lastQ) {
          const lastOpts = (lastQ.answer_options || []).filter(o => lastAnswer.selected_option_ids.includes(o.id));
          const targets = lastOpts.filter(o => o.branch_rule?.action === 'show_question' || o.branch_rule?.action === 'skip_to')
            .map(o => o.branch_rule.target_question_id).filter(Boolean);
          if (targets.length > 0) nextQ = questions.find(q => q.id === targets[0]);
          else if (!lastOpts.some(o => o.branch_rule?.action === 'end')) {
            const idx = questions.findIndex(q => q.id === lastQ.id);
            nextQ = questions[idx + 1] || null;
          }
        }
      }

      if (nextQ) {
        setQuestion(nextQ);
        const prevMatch = (runData.previous_answers || []).find(a => a.question_id === nextQ.id);
        const existMatch = (runData.answers || []).find(a => a.question_id === nextQ.id);
        if (prevMatch) setSelected(prevMatch.selected_option_ids);
        else if (existMatch) setSelected(existMatch.selected_option_ids);
        else setSelected([]);
      } else {
        navigate(`/checklists/${runId}/complete`);
      }
    } catch {
      navigate('/checklists');
    } finally {
      setLoading(false);
    }
  }, [runId, navigate, user]);

  useEffect(() => { loadRun(); }, [loadRun]);

  const handleProjectFieldsSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res = await api.patch(`/runs/${runId}/project-fields`, { project_fields: projectFields });
      setRun(res.data); setNeedsProjectFields(false);
      const questions = (template.questions || []).sort((a, b) => a.order - b.order);
      const firstQ = questions[0];
      if (firstQ) {
        setQuestion(firstQ);
        const prevMatch = (res.data.previous_answers || []).find(a => a.question_id === firstQ.id);
        setSelected(prevMatch ? prevMatch.selected_option_ids : []);
      }
    } catch (err) { toast.error(err.response?.data?.detail || 'Error saving project fields'); }
    finally { setSubmitting(false); }
  };

  const handleSelect = (optionId) => {
    if (!question) return;
    if (question.type === 'multi_choice') {
      setSelected(prev => prev.includes(optionId) ? prev.filter(id => id !== optionId) : [...prev, optionId]);
    } else {
      setSelected([optionId]);
    }
  };

  const handleNext = async () => {
    if (selected.length === 0 || !question) return;
    setSubmitting(true);
    try {
      const res = await api.post(`/runs/${runId}/answer`, { question_id: question.id, selected_option_ids: selected });
      if (res.data.run_complete) {
        navigate(`/checklists/${runId}/complete`);
      } else if (res.data.next_question) {
        setRun(res.data.run); setQuestion(res.data.next_question);
        const prevMatch = (res.data.run.previous_answers || []).find(a => a.question_id === res.data.next_question.id);
        const existMatch = (res.data.run.answers || []).find(a => a.question_id === res.data.next_question.id);
        if (prevMatch) setSelected(prevMatch.selected_option_ids);
        else if (existMatch) setSelected(existMatch.selected_option_ids);
        else setSelected([]);
      } else {
        navigate(`/checklists/${runId}/complete`);
      }
    } catch (err) { toast.error(err.response?.data?.detail || 'Error submitting answer'); }
    finally { setSubmitting(false); }
  };

  const handleBack = async () => {
    if (!run?.answers?.length || !question) return;
    const currentIdx = run.answers.findIndex(a => a.question_id === question.id);
    const targetIdx = currentIdx > 0 ? currentIdx : run.answers.length - 1;
    const targetQ = run.answers[targetIdx]?.question_id;
    if (!targetQ) return;
    try {
      const res = await api.post(`/runs/${runId}/back`, { to_question_id: targetQ });
      setRun(res.data.run); setQuestion(res.data.current_question);
      const prevMatch = (res.data.run.previous_answers || []).find(a => a.question_id === res.data.current_question.id);
      setSelected(prevMatch ? prevMatch.selected_option_ids : []);
    } catch (err) { toast.error(err.response?.data?.detail || 'Error going back'); }
  };

  if (loading) return <div className="flex items-center justify-center h-full text-sm text-zinc-400">Loading...</div>;

  if (needsProjectFields && template) {
    return (
      <div className="max-w-lg mx-auto px-6 py-10" data-testid="duplicate-project-fields">
        {versionWarning && (
          <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 text-amber-800 text-xs font-medium px-3 py-2.5 rounded-sm mb-4" data-testid="version-warning">
            <AlertTriangle size={14} /> Template has been updated since the original run. Please review all answers carefully.
          </div>
        )}
        <h2 className="text-lg font-bold tracking-tight text-zinc-900 mb-1">Enter project details</h2>
        <p className="text-sm text-zinc-500 mb-5">This is a duplicated checklist. Enter new project details to continue.</p>
        <form onSubmit={handleProjectFieldsSubmit}>
          {(template.project_fields || []).sort((a, b) => a.order - b.order).map(f => (
            <div key={f.key} className="mb-3">
              <Label className="text-xs font-semibold text-zinc-600 mb-1 block">{f.label} {f.required && <span className="text-red-400">*</span>}</Label>
              <Input value={projectFields[f.key] || ''} onChange={e => setProjectFields({ ...projectFields, [f.key]: e.target.value })}
                data-testid={`field-${f.key}`} className="rounded-sm h-9" placeholder={f.placeholder || f.label} required={f.required} />
            </div>
          ))}
          <Button type="submit" disabled={submitting} data-testid="submit-project-fields" className="w-full rounded-sm h-10 text-sm font-semibold mt-2"
            style={{ background: 'var(--brand-primary, #1C3557)' }}>
            {submitting ? 'Saving...' : 'Continue to questions'}
          </Button>
        </form>
      </div>
    );
  }

  if (!question) return <div className="flex items-center justify-center h-full text-sm text-zinc-400">No question available</div>;

  const totalQuestions = (template?.questions || []).length;
  const answeredCount = run?.answers?.length || 0;
  const progress = Math.min((answeredCount / Math.max(totalQuestions, 1)) * 100, 95);

  return (
    <div className="max-w-2xl mx-auto px-6 py-10" data-testid="run-question-page">
      <Progress value={progress} className="h-1.5 mb-8 rounded-sm [&>div]:rounded-sm" style={{ '--tw-bg-opacity': 1 }} />

      {versionWarning && (
        <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 text-amber-800 text-xs font-medium px-3 py-2.5 rounded-sm mb-4" data-testid="version-warning">
          <AlertTriangle size={14} /> This checklist is based on a template that has been updated. Please review all answers carefully.
          <button onClick={() => setVersionWarning(false)} className="ml-auto text-amber-500 hover:text-amber-700 text-xs font-semibold">Dismiss</button>
        </div>
      )}

      {run?.previous_answers?.length > 0 && (
        <div className="text-[11px] font-medium text-blue-600 bg-blue-50 border border-blue-200 px-3 py-2 rounded-sm mb-4">
          Duplicated run — answers pre-selected from previous run for your review
        </div>
      )}

      <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-400 mb-2">Question {answeredCount + 1} of {totalQuestions}</div>
      <h2 className="text-xl font-bold tracking-tight text-zinc-900 mb-1" data-testid="question-text">{question.text}</h2>
      {question.help_text && <p className="text-sm text-zinc-500 mb-4">{question.help_text}</p>}

      <div className="space-y-2 mt-6 mb-10">
        {(question.answer_options || []).sort((a, b) => a.order - b.order).map(opt => (
          <button key={opt.id} onClick={() => handleSelect(opt.id)} data-testid={`option-${opt.id}`}
            className={`w-full text-left px-5 py-4 border rounded-sm text-sm font-medium flex items-center gap-3 transition-all duration-150 ${
              selected.includes(opt.id) ? 'border-2 border-zinc-900 bg-zinc-50' : 'border-zinc-200 bg-white hover:border-zinc-300'
            }`}>
            {question.type === 'multi_choice' ? (
              <div className={`w-5 h-5 rounded-sm border-2 flex-shrink-0 flex items-center justify-center transition-colors ${
                selected.includes(opt.id) ? 'border-zinc-900 bg-zinc-900' : 'border-zinc-300'
              }`}>
                {selected.includes(opt.id) && <svg width="12" height="12" viewBox="0 0 12 12"><path d="M2 6l3 3 5-5" stroke="white" strokeWidth="2" fill="none"/></svg>}
              </div>
            ) : (
              <div className={`w-5 h-5 rounded-full border-2 flex-shrink-0 flex items-center justify-center transition-colors ${
                selected.includes(opt.id) ? 'border-zinc-900' : 'border-zinc-300'
              }`}>
                {selected.includes(opt.id) && <div className="w-2.5 h-2.5 rounded-full bg-zinc-900" />}
              </div>
            )}
            {opt.label}
          </button>
        ))}
      </div>

      <div className="flex items-center justify-between">
        <Button variant="outline" onClick={handleBack} disabled={!run?.answers?.length} data-testid="back-btn" className="rounded-sm gap-1.5">
          <ArrowLeft size={14} /> Back
        </Button>
        <Button onClick={handleNext} disabled={selected.length === 0 || submitting} data-testid="next-btn" className="rounded-sm gap-1.5 px-6"
          style={{ background: 'var(--brand-primary, #1C3557)' }}>
          {submitting ? 'Saving...' : 'Next'} <ArrowRight size={14} />
        </Button>
      </div>
    </div>
  );
}
